import React from "react";
import Legal from "../components/layout/Legal";

const PrivacyPolicy = () => {
  return (
    <Legal pageTitle="Privacy Policy" variant="enterprise">
      <div className="flex flex-col gap-12">
        <div>
          <p className="b1 text-grey-08 mb-4 font-google">1. Introduction</p>
          <p className="b2 text-grey-05">
            Computechoice.com (we, us, our) is committed to protecting your
            personal data. This Privacy Policy explains how we collect, use,
            store, and share information when you visit our Site or interact
            with campaigns hosted on it. It also explains your rights in
            relation to your personal data.
          </p>
          <p className="b2 text-grey-05 mt-2">
            By using the Site or submitting information through any form on the
            Site, you acknowledge that you have read and understood this Privacy
            Policy.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">2. Who We Are</p>
          <p className="b2 text-grey-05">
            Computechoice.com operates as a B2B technology marketing platform
            hosting campaigns on behalf of enterprise technology vendors and
            their authorised channel partners. Where we process personal data on
            behalf of a campaign client, we do so as a data processor acting
            under the instructions of that client.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            3. Information We Collect
          </p>

          <p className="b2 text-grey-05 font-medium mb-2">
            3.1 Information you provide directly
          </p>
          <p className="b2 text-grey-05 mb-2">
            When you complete a form on the Site, we may collect:
          </p>
          <ul className="b2 text-grey-05 list-disc ps-6 space-y-1">
            <li>Your name and job title</li>
            <li>Your work email address</li>
            <li>Your company name and industry</li>
            <li>Your phone number (where optionally provided)</li>
            <li>Your responses to any assessment or qualification questions</li>
          </ul>

          <p className="b2 text-grey-05 font-medium mt-4 mb-2">
            3.2 Information collected automatically
          </p>
          <p className="b2 text-grey-05 mb-2">
            When you visit the Site, we may automatically collect:
          </p>
          <ul className="b2 text-grey-05 list-disc ps-6 space-y-1">
            <li>IP address and approximate location</li>
            <li>Browser type and version</li>
            <li>Device type and operating system</li>
            <li>Pages visited and time spent on the Site</li>
            <li>Referring URL and exit pages</li>
            <li>UTM parameters and campaign tracking identifiers</li>
          </ul>

          <p className="b2 text-grey-05 font-medium mt-4 mb-2">
            3.3 Cookies and tracking technologies
          </p>
          <p className="b2 text-grey-05">
            We use cookies, pixels, and similar technologies to operate the
            Site, understand traffic patterns, and measure campaign performance.
            We use Google Analytics 4 (GA4) and may use third-party testing and
            analytics tools including Statsig. You may decline non-essential
            cookies through your browser settings. Declining cookies may affect
            certain features of the Site.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            4. How We Use Your Information
          </p>
          <p className="b2 text-grey-05 mb-2">
            We use the information we collect for the following purposes:
          </p>
          <ul className="b2 text-grey-05 list-disc ps-6 space-y-1">
            <li>
              To deliver the content, resources, or downloads you requested
            </li>
            <li>
              To contact you with information about HPE products and services
              relevant to your enquiry, where you have provided consent
            </li>
            <li>
              To pass your details to the relevant campaign client or their
              authorised channel partner for follow-up, as disclosed at the
              point of collection
            </li>
            <li>
              To measure and improve campaign performance and Site functionality
            </li>
            <li>To comply with applicable legal obligations</li>
            <li>To detect and prevent fraud or misuse of the Site</li>
          </ul>
          <p className="b2 text-grey-05 mt-2">
            We do not use your personal data for automated decision-making or
            profiling that produces legal or similarly significant effects.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            5. Legal Basis for Processing
          </p>
          <p className="b2 text-grey-05 mb-2">
            Where data protection law requires a legal basis for processing, we
            rely on the following:
          </p>
          <ul className="b2 text-grey-05 list-disc ps-6 space-y-1">
            <li>
              Consent: Where you have actively submitted a form and agreed to be
              contacted
            </li>
            <li>
              Legitimate interests: For analytics, Site improvement, and fraud
              prevention, where these interests are not overridden by your
              rights
            </li>
            <li>
              Legal obligation: Where we are required to process data to comply
              with applicable law
            </li>
          </ul>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            6. Data Sharing and Disclosure
          </p>

          <p className="b2 text-grey-05 font-medium mb-2">
            6.1 Campaign clients and channel partners
          </p>
          <p className="b2 text-grey-05 mb-2">
            If you interact with a campaign on the Site, your contact
            information and form responses will be shared with the client
            running that campaign and their authorised channel partners for the
            purpose of follow-up communications related to the products or
            services featured.
          </p>

          <p className="b2 text-grey-05 font-medium mb-2">
            6.2 Service providers
          </p>
          <p className="b2 text-grey-05 mb-2">
            We share data with third-party service providers who assist us in
            operating the Site, hosting content, managing campaigns, and
            analysing performance. These providers are contractually required to
            process data only on our instructions and in accordance with
            applicable privacy standards.
          </p>

          <p className="b2 text-grey-05 font-medium mb-2">
            6.3 Analytics and marketing tools
          </p>
          <p className="b2 text-grey-05 mb-2">
            We use Google Analytics 4 for Site measurement. Data is processed in
            accordance with Google&apos;s privacy terms. We may also use
            Saleshandy for email campaign management and Statsig for A/B
            testing. Each tool processes data under its own applicable terms and
            data processing agreements.
          </p>

          <p className="b2 text-grey-05 font-medium mb-2">
            6.4 Legal and regulatory requirements
          </p>
          <p className="b2 text-grey-05 mb-2">
            We may disclose your personal data to regulatory authorities, law
            enforcement, or other third parties where required by applicable law
            or where necessary to protect the rights, property, or safety of
            Computechoice.com, our clients, or others.
          </p>

          <p className="b2 text-grey-05 font-medium">
            6.5 We do not sell your data.
          </p>
          <p className="b2 text-grey-05">
            We do not sell, rent, or trade your personal information to third
            parties for their independent marketing purposes.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            7. International Data Transfers
          </p>
          <p className="b2 text-grey-05">
            Our operations and service providers are located across multiple
            countries. Where we transfer personal data outside your
            jurisdiction, we take appropriate steps to ensure that such
            transfers comply with applicable data protection law, including
            through the use of standard contractual clauses or other lawful
            transfer mechanisms.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">8. Data Retention</p>
          <p className="b2 text-grey-05">
            We retain personal data only for as long as necessary to fulfil the
            purposes set out in this Policy, or as required by applicable law.
            Contact information provided through campaign forms is typically
            retained for the duration of the relevant campaign and for a
            reasonable period thereafter to support follow-up and reporting
            obligations. You may request deletion of your data at any time as
            described in Section 9.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">9. Your Rights</p>
          <p className="b2 text-grey-05 mb-2">
            Depending on your jurisdiction, you may have the following rights in
            relation to your personal data:
          </p>
          <ul className="b2 text-grey-05 list-disc ps-6 space-y-1">
            <li>The right to access the personal data we hold about you</li>
            <li>
              The right to request correction of inaccurate or incomplete data
            </li>
            <li>
              The right to request erasure of your data (the right to be
              forgotten)
            </li>
            <li>The right to object to or restrict processing of your data</li>
            <li>The right to data portability</li>
            <li>
              The right to withdraw consent at any time, without affecting the
              lawfulness of processing prior to withdrawal
            </li>
            <li>
              The right to lodge a complaint with your local data protection
              authority
            </li>
          </ul>
          <p className="b2 text-grey-05 mt-2">
            To exercise any of these rights, please contact us at
            privacy@computechoice.com. We will respond within the timeframe
            required by applicable law.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">10. Data Security</p>
          <p className="b2 text-grey-05">
            We implement appropriate technical and organisational measures to
            protect your personal data against unauthorised access, disclosure,
            alteration, or destruction. These measures include encrypted data
            transmission, access controls, and secure hosting environments. No
            method of transmission over the internet is entirely secure, and we
            cannot guarantee absolute security.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">11. Children</p>
          <p className="b2 text-grey-05">
            The Site is intended for use by business professionals and is not
            directed at individuals under the age of 18. We do not knowingly
            collect personal data from minors.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">
            12. Changes to This Policy
          </p>
          <p className="b2 text-grey-05">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, legal requirements, or operational
            circumstances. The Last Updated date at the top of this page will be
            revised accordingly. We encourage you to review this Policy
            periodically.
          </p>
        </div>

        <div>
          <p className="b1 text-grey-08 mb-4 font-google">13. Contact</p>
          <p className="b2 text-grey-05">
            For questions, requests, or concerns regarding this Privacy Policy
            or our data practices, please contact:
          </p>
          <p className="b2 text-grey-05 mt-2">Computechoice.com</p>
          <p className="b2 text-grey-05">privacy@computechoice.com</p>
        </div>
      </div>
    </Legal>
  );
};

export default PrivacyPolicy;
